/** Paths that do not require authentication. */
export const PUBLIC_PATHS = [
  '/',
  '/product',
  '/login',
  '/auth/callback',
  '/features',
  '/pricing',
  '/privacy',
  '/terms',
  '/about',
  '/ai',
  '/for-students',
  '/for-developers',
  '/for-interview-prep',
  '/for-teams',
  '/contact',
  '/security',
  '/cookies',
  '/refund',
];

export function isPublicQuestionPath(pathname) {
  if (!pathname) return false;
  return /^\/q\/\d+\/?$/.test(pathname.split('?')[0]);
}

export function isPublicPath(pathname) {
  if (!pathname) return false;
  if (isPublicQuestionPath(pathname)) return true;
  return PUBLIC_PATHS.some((p) => pathname === p || (p !== '/' && pathname.startsWith(`${p}/`)));
}

/** Direct question links (e.g. /question/2269) — load content without onboarding gate. */
export function isQuestionDetailPath(pathname) {
  if (!pathname) return false;
  return /^\/question\/\d+\/?$/.test(pathname.split('?')[0]);
}

/** Paths that are worth returning to after login (not the marketing site). */
const POST_LOGIN_KEEP_PATHS = ['/pricing', '/checkout', '/checkout/success', '/checkout/cancel'];

/** Marketing/legal pages signed-in users should not open via URL bar. */
export function isMarketingSitePath(pathname) {
  if (!pathname) return false;
  if (isPublicQuestionPath(pathname)) return false;
  if (pathname === '/login' || pathname === '/auth/callback') return false;
  const isKeep = POST_LOGIN_KEEP_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
  if (isKeep) return false;
  return isPublicPath(pathname);
}

export function isOnboardingCompleteLocally() {
  try {
    return localStorage.getItem('qnaread.onboardingDone') === '1';
  } catch {
    return false;
  }
}

/** Default signed-in landing (matches app nav “Home”). */
export const DEFAULT_APP_HOME = '/dashboard';

function isMarketingOrPublicAppEntry(pathname) {
  if (!pathname) return true;
  if (pathname === '/' || pathname === '/login' || pathname === '/auth/callback') return true;
  return isPublicPath(pathname) && !POST_LOGIN_KEEP_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

/**
 * Where to send the user after a successful login/sign-in.
 * Marketing/public URLs become DEFAULT_APP_HOME (checkout flows can keep /pricing).
 */
export function resolvePostLoginDestination(redirectPath) {
  let path = (redirectPath || '').trim();
  if (!path.startsWith('/') || path.startsWith('//')) {
    return DEFAULT_APP_HOME;
  }

  const pathname = path.split('?')[0];
  const isKeep = POST_LOGIN_KEEP_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );

  if (isMarketingOrPublicAppEntry(pathname) && !isKeep) {
    return DEFAULT_APP_HOME;
  }

  if (pathname.startsWith('/settings')) {
    return path.includes('?') ? path : '/settings?tab=billing';
  }

  return path;
}

/** Persist ?redirect= across Google popup / slow navigations so post-login routing is stable. */
export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

export function rememberPostLoginRedirect(redirectPath) {
  if (!redirectPath) return;
  sessionStorage.setItem(
    POST_LOGIN_REDIRECT_KEY,
    resolvePostLoginDestination(redirectPath)
  );
}

export function consumePostLoginRedirect() {
  const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  if (stored) {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return resolvePostLoginDestination(stored);
  }
  return null;
}

export function captureLoginRedirectFromLocation(search) {
  const params = new URLSearchParams(search || '');
  const raw = params.get('redirect');
  if (raw) {
    rememberPostLoginRedirect(raw);
  }
}
